import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaTrash,
  FaBalanceScale,
  FaExclamationTriangle
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import LoadingState from "../../components/LoadingState";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, serverTimestamp } from "firebase/firestore";
import { useToast } from "../../components/Toast";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay,
} from "../../utils/designTokens";

const CriteriosDivisionGastos = ({ setSelected }) => {
  const { showToast } = useToast();
  const [criteriaList, setCriteriaList] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [form, setForm] = useState({
    field: "",
    criteria: "",
  });

  const fetchFieldOptions = async () => {
    setLoadingFields(true);
    try {
      const snap = await getDocs(collection(db, "fieldsapartments"));
      const data = snap.docs.map((doc) => {
        const docData = doc.data();
        const value = docData.value || docData.field || docData.name || docData.id || doc.id;
        const label = docData.label || docData.name || docData.field || docData.value || doc.id;
        return { value: String(value), label: String(label) };
      });
      
      if (data.length === 0) {
        setFieldOptions([
          { value: "all", label: "Todos" },
          { value: "tenant", label: "Inquilinos" },
          { value: "owner", label: "Propietarios" },
          { value: "garage", label: "Garage" },
          { value: "balcony", label: "Balcón" },
          { value: "bedroom", label: "Dormitorios" },
          { value: "bathroom", label: "Baños" },
          { value: "m2", label: "Metros cuadrados" },
          { value: "floor", label: "Piso" },
        ]);
      } else {
        setFieldOptions(data);
      }
    } catch (error) {
      console.error("Error cargando campos:", error);
      setFieldOptions([
        { value: "all", label: "Todos" },
        { value: "tenant", label: "Inquilinos" },
        { value: "owner", label: "Propietarios" },
        { value: "garage", label: "Garage" },
        { value: "balcony", label: "Balcón" },
        { value: "bedroom", label: "Dormitorios" },
        { value: "bathroom", label: "Baños" },
        { value: "m2", label: "Metros cuadrados" },
        { value: "floor", label: "Piso" },
      ]);
    } finally {
      setLoadingFields(false);
    }
  };

  const fetchCriteria = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "criteriaexpenses"));
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCriteriaList(data);
    } catch (error) {
      console.error("Error cargando criterios:", error);
      setNotification({ type: "error", message: "Error al cargar los criterios" });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriteria();
    fetchFieldOptions();
  }, []);

  const handleAddCriteria = async () => {
    if (!form.field || !form.criteria.trim()) {
      setNotification({ type: "error", message: "Por favor, completa todos los campos" });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    // Verificar duplicados antes de agregar
    const duplicate = criteriaList.find(
      (c) =>
        c.field === form.field &&
        (c.criteria || "").toLowerCase().trim() === form.criteria.trim().toLowerCase()
    );
    if (duplicate) {
      showToast(
        `Ya existe un criterio con el campo "${form.field}" y nombre "${form.criteria.trim()}". No se permiten duplicados.`,
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      await addDoc(collection(db, "criteriaexpenses"), {
        field: form.field,
        criteria: form.criteria.trim(),
        createdAt: serverTimestamp(),
      });

      setNotification({ type: "success", message: "Criterio agregado exitosamente" });
      setTimeout(() => setNotification(null), 4000);
      setForm({ field: "", criteria: "" });
      setShowAddModal(false);
      fetchCriteria();
    } catch (error) {
      console.error("Error agregando criterio:", error);
      setNotification({ type: "error", message: "Error al agregar el criterio" });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCriteria = async (id) => {
    try {
      // Verificar si hay gastos que referencian este criterio
      const expensesQ = query(
        collection(db, "expenses"),
        where("criteria", "==", id)
      );
      const expensesSnap = await getDocs(expensesQ);
      const referencingExpenses = expensesSnap.size;

      const message = referencingExpenses > 0
        ? `Este criterio está siendo usado por ${referencingExpenses} gasto(s) existente(s). Si lo eliminás, esos gastos quedarán sin criterio de distribución asignado. ¿Querés eliminarlo de todas formas?`
        : "¿Estás seguro de eliminar este criterio?";

      setConfirmModal({
        message,
        onConfirm: async () => {
          setConfirmModal(null);
          try {
            await deleteDoc(doc(db, "criteriaexpenses", id));
            setNotification({ type: "success", message: "Criterio eliminado exitosamente" });
            setTimeout(() => setNotification(null), 4000);
            fetchCriteria();
          } catch (error) {
            console.error("Error eliminando criterio:", error);
            setNotification({ type: "error", message: "Error al eliminar el criterio" });
            setTimeout(() => setNotification(null), 4000);
          }
        },
      });
    } catch (error) {
      console.error("Error verificando criterio:", error);
      setNotification({ type: "error", message: "Error al verificar el criterio" });
      setTimeout(() => setNotification(null), 4000);
    }
  };

  return (
    <div style={styles.page}>
      {notification && (
        <div style={{
          padding: "12px 16px",
          marginBottom: spacing[4],
          borderRadius: radius.sm,
          backgroundColor: notification.type === "error" ? colors.errorLight : colors.successLight,
          color: notification.type === "error" ? colors.errorDark : colors.success,
          border: `1px solid ${notification.type === "error" ? colors.errorLight : colors.successLight}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: typography.sizes.md,
        }}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "inherit" }} aria-label="Cerrar notificación">×</button>
        </div>
      )}
      {/* Header con título y acciones */}
      <div style={styles.pageHeader}>
        <div style={styles.headerTitleSection}>
          <h2 style={styles.pageTitle}>Criterios de División</h2>
          <p style={styles.pageSubtitle}>{criteriaList.length} criterios configurados</p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <FaPlus size={14} /> Agregar Criterio
        </button>
      </div>

      {/* Content */}
      <div style={styles.card}>
        {loading ? (
          <LoadingState message="Cargando criterios…" />
        ) : criteriaList.length === 0 ? (
          <div style={styles.emptyState}>
            <FaBalanceScale size={40} color={colors.gray300} />
            <p style={styles.emptyTitle}>No hay criterios configurados</p>
            <p style={styles.emptySubtext}>Hacé clic en "Agregar Criterio" para comenzar.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Nombre (Criteria)</th>
                  <th style={styles.th}>Campo (Field)</th>
                  <th style={{ ...styles.th, width: 80 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {criteriaList.map((item) => (
                  <tr key={item.id} style={styles.tr}>
                    <td style={styles.td}>{item.criteria || "—"}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{item.field || "—"}</span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.deleteBtn}
                        onClick={() => handleDeleteCriteria(item.id)}
                        title="Eliminar criterio"
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Agregar Criterio */}
      <AdminModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setForm({ field: "", criteria: "" });
        }}
        title="Agregar Criterio"
        icon={<FaBalanceScale />}
        size="narrow"
        footer={
          <>
            <button
              style={styles.secondaryBtn}
              onClick={() => {
                setShowAddModal(false);
                setForm({ field: "", criteria: "" });
              }}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              style={styles.primaryBtn}
              onClick={handleAddCriteria}
              disabled={saving || !form.field || !form.criteria.trim()}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Campo (Field) *</label>
          <select
            style={styles.input}
            value={form.field}
            onChange={(e) => setForm({ ...form, field: e.target.value })}
            disabled={loadingFields}
          >
            <option value="">
              {loadingFields ? "Cargando campos..." : "Seleccionar campo..."}
            </option>
            {fieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre (Criteria) *</label>
          <input
            type="text"
            style={styles.input}
            value={form.criteria}
            onChange={(e) => setForm({ ...form, criteria: e.target.value })}
            placeholder="Ej: Todos"
          />
        </div>
      </AdminModal>

      {/* Modal Confirmar Eliminación */}
      <AdminModal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title="Confirmar acción"
        size="narrow"
        footer={
          <>
            <button onClick={() => setConfirmModal(null)} style={{ ...btnSecondary, padding: "8px 16px" }}>Cancelar</button>
            <button onClick={confirmModal?.onConfirm} style={{ ...btnDanger, padding: "8px 16px" }}>Confirmar</button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: typography.sizes.md, color: colors.gray600 }}>{confirmModal?.message}</p>
      </AdminModal>
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  page: {
    ...pageContainer,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  addBtn: {
    ...btnPrimary,
  },
  // Tarjeta principal — sin borde, solo sombra
  card: {
    backgroundColor: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  emptyState: {
    textAlign: "center",
    padding: `${spacing[12]}px ${spacing[6]}px`,
    color: colors.gray600,
  },
  emptyTitle: {
    margin: "16px 0 4px",
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.gray900,
  },
  emptySubtext: {
    margin: 0,
    fontSize: typography.sizes.md,
    color: colors.gray400,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "14px 20px",
    backgroundColor: colors.gray50,
    fontWeight: 400,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${colors.gray200}`,
  },
  tr: {
    borderBottom: `1px solid ${colors.gray100}`,
  },
  td: {
    padding: "14px 20px",
    fontSize: typography.sizes.md,
    color: colors.gray900,
  },
  badge: {
    display: "inline-block",
    padding: "4px 10px",
    backgroundColor: colors.primarySubtle,
    color: colors.primary,
    borderRadius: 6,
    fontSize: typography.sizes.base,
    fontWeight: 400,
  },
  deleteBtn: {
    ...btnDanger,
    padding: "8px 10px",
    borderRadius: 6,
  },
  formGroup: {
    marginBottom: spacing[5],
  },
  label: {
    display: "block",
    marginBottom: spacing[2],
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
  },
  input: {
    ...inputBase,
    backgroundColor: colors.gray50,
    boxSizing: "border-box",
  },
  secondaryBtn: {
    ...btnSecondary,
  },
  primaryBtn: {
    ...btnPrimary,
  },
};

export default CriteriosDivisionGastos;
